/* Nordic Drum Store — small progressive enhancements. Everything works without JS. */

(() => {
  // ---------- Variant pickers: shared helper ----------
  // Names the chosen value (e.g. "Farve: Navy") and crosses out values that are
  // sold out in combination with the other current choices.
  const syncOptions = (scope, inputs, variants, chosen) => {
    inputs.forEach((input) => {
      const index = Number(input.dataset.option);
      const available = variants.some((v) =>
        v.available && v.options.every((o, j) => (j === index ? o === input.value : o === chosen[j]))
      );
      input.closest('.pill, .swatch-pick')?.classList.toggle('is-unavailable', !available);
    });
    scope.querySelectorAll('[data-option-label]').forEach((el) => {
      el.textContent = chosen[Number(el.dataset.optionLabel)] || '';
    });
  };

  // ---------- Back to top: appears after scrolling half a screen (by then the hero's
  // bottom-right "Bagom os" button has moved out of the way) ----------
  const toTop = document.querySelector('[data-back-to-top]');
  if (toTop) {
    const toggle = () => {
      const show = window.scrollY > window.innerHeight * 0.5;
      toTop.classList.toggle('is-visible', show);
      toTop.tabIndex = show ? 0 : -1;
    };
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    toTop.addEventListener('click', () => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      document.getElementById('main')?.focus({ preventScroll: true });
    });
  }

  // ---------- Homepage brands: arrows move the row one card at a time ----------
  // The arrows only show when there are more brands than fit, and fade at either end.
  document.querySelectorAll('[data-lineup]').forEach((row) => {
    const nav = row.parentElement.querySelector('[data-lineup-nav]');
    if (!nav) return;
    const prev = nav.querySelector('[data-lineup-prev]');
    const next = nav.querySelector('[data-lineup-next]');
    const cards = [...row.children];
    const pos = (card) => card.offsetLeft - cards[0].offsetLeft;
    // While a slide is under way, quick repeat clicks count from where it is heading
    let target = null;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      if (target !== null && Math.abs(row.scrollLeft - target) < 2) target = null;
      const x = target ?? row.scrollLeft;
      nav.hidden = max <= 1;
      prev.disabled = x <= 1;
      next.disabled = x >= max - 1;
    };
    const step = (direction) => {
      const max = row.scrollWidth - row.clientWidth;
      const x = target ?? row.scrollLeft;
      const current = cards.reduce((best, card, i) =>
        (Math.abs(pos(card) - x) < Math.abs(pos(cards[best]) - x) ? i : best), 0);
      const i = Math.min(Math.max(current + direction, 0), cards.length - 1);
      target = Math.min(pos(cards[i]), max);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
      update();
    };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    row.addEventListener('scroll', update, { passive: true });
    row.addEventListener('scrollend', () => { target = null; update(); });
    new ResizeObserver(update).observe(row);
    update();
  });

  // ---------- Header: measurements, transparent-over-hero, solid on scroll ----------
  const header = document.querySelector('[data-header]');
  if (header) {
    const root = document.documentElement;
    const headerSection = header.closest('.shopify-section') || header;
    const measure = () => {
      root.style.setProperty('--header-height', `${header.offsetHeight}px`);
      root.style.setProperty('--announcement-height', `${headerSection.offsetTop}px`);
    };
    measure();
    new ResizeObserver(measure).observe(header);
    window.addEventListener('resize', measure);

    // Only stay transparent if the page actually starts with a hero.
    if (header.classList.contains('site-header--transparent') && !document.querySelector('main > .section-hero:first-child')) {
      header.classList.remove('site-header--transparent');
    }

    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ---------- Announcement bar: vertical carousel ----------
  document.querySelectorAll('[data-announcements]').forEach((list) => {
    const items = [...list.children];
    if (items.length < 2) return;
    const delay = (Number(list.dataset.interval) || 4) * 1000;
    let index = 0;
    let timer;
    const show = (next) => {
      if (next === index) return;
      const current = items[index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      setTimeout(() => current.classList.remove('is-leaving'), 650);
      items[next].classList.add('is-active');
      index = next;
    };
    const start = () => {
      clearInterval(timer);
      timer = setInterval(() => show((index + 1) % items.length), delay);
    };
    const stop = () => clearInterval(timer);
    list.addEventListener('mouseenter', stop);
    list.addEventListener('mouseleave', start);
    list.addEventListener('focusin', stop);
    list.addEventListener('focusout', start);
    // Theme editor: show the message being edited.
    document.addEventListener('shopify:block:select', (e) => {
      const i = items.indexOf(e.target);
      if (i > -1) { stop(); show(i); }
    });
    document.addEventListener('shopify:block:deselect', start);
    start();
  });

  // ---------- Video pop-up (hero "watch the story") ----------
  document.querySelectorAll('[data-video-open]').forEach((btn) => {
    const modal = document.getElementById(btn.dataset.videoOpen);
    if (!modal) return;
    const frame = modal.querySelector('[data-video-frame]');
    const template = modal.querySelector('[data-video-template]');
    btn.addEventListener('click', () => {
      frame.replaceChildren(template.content.cloneNode(true));
      modal.showModal();
    });
    // Remove the player on close so the video stops.
    modal.addEventListener('close', () => frame.replaceChildren());
    modal.querySelector('[data-video-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.close();
    });
  });

  // ---------- Menu: mark the current category ----------
  // Category links point to "all products" filtered by product type. A link is
  // marked current when the page shows exactly that category, however the
  // filter was set (menu, homepage tile, filter panel) and whatever else is
  // filtered too. "All products" is current when no category is filtered.
  {
    const TYPE = 'filter.p.product_type';
    const here = new URL(location.href);
    const hereTypes = here.searchParams.getAll(TYPE);
    document.querySelectorAll('.site-nav__link[href], .mobile-nav__list a[href]').forEach((a) => {
      const target = new URL(a.getAttribute('href'), location.origin);
      const targetTypes = target.searchParams.getAll(TYPE);
      let current = false;
      if (target.pathname === here.pathname) {
        if (targetTypes.length) current = hereTypes.length === 1 && hereTypes[0] === targetTypes[0];
        else current = hereTypes.length === 0 && target.search === '' ;
      }
      if (current) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  }

  // ---------- Mobile menu ----------
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const openBtn = document.querySelector('[data-menu-open]');
  const closeBtn = document.querySelector('[data-menu-close]');

  const setMenu = (open) => {
    if (!mobileNav) return;
    mobileNav.open = open;
    openBtn?.setAttribute('aria-expanded', String(open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) closeBtn?.focus();
    else openBtn?.focus();
  };
  openBtn?.addEventListener('click', () => setMenu(true));
  closeBtn?.addEventListener('click', () => setMenu(false));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileNav?.open) setMenu(false);
  });

  // ---------- Desktop dropdowns: close others / on outside click ----------
  const dropdowns = [...document.querySelectorAll('[data-dropdown]')];
  dropdowns.forEach((d) =>
    d.addEventListener('toggle', () => {
      if (d.open) dropdowns.filter((o) => o !== d).forEach((o) => (o.open = false));
    })
  );
  document.addEventListener('click', (e) => {
    dropdowns.forEach((d) => {
      if (d.open && !d.contains(e.target)) d.open = false;
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') dropdowns.forEach((d) => (d.open = false));
  });

  // On devices with a mouse, open on hover; a short delay on leaving lets the
  // pointer travel from the menu item down into the panel.
  if (window.matchMedia('(hover: hover)').matches) {
    dropdowns.forEach((d) => {
      const item = d.closest('.site-nav__item') || d;
      const panel = d.querySelector('.mega, .site-nav__dropdown');
      let closeTimer;
      const open = () => { clearTimeout(closeTimer); d.open = true; };
      const close = () => { closeTimer = setTimeout(() => (d.open = false), 160); };
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);
      panel?.addEventListener('mouseenter', open);
      panel?.addEventListener('mouseleave', close);
      // A mouse click on the label keeps it open instead of toggling it shut;
      // keyboard activation (detail === 0) still toggles.
      d.querySelector('summary')?.addEventListener('click', (e) => {
        if (e.detail === 0) return; // keyboard: toggle as normal
        e.preventDefault();
        // With a mouse, hover opens the panel; a click goes to the item's own page
        if (d.dataset.url) window.location.href = d.dataset.url;
      });
    });
  }

  // ---------- Product cards: photo arrows, option picker, quick add ----------
  const money = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatMoney = (cents) => `${money.format(cents / 100)} kr`;

  const updateCartCount = async () => {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      document.querySelectorAll('a[href$="/cart"].icon-btn').forEach((link) => {
        let badge = link.querySelector('.cart-count');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cart-count';
          link.appendChild(badge);
        }
        badge.textContent = cart.item_count;
      });
    } catch (_) { /* the normal page load will show the right count */ }
  };

  // Add to cart without leaving the page (Shopify's /cart/add.js); falls back to a normal post.
  const ajaxAdd = async (form, button, onDone) => {
    try {
      const res = await fetch(`${form.action}.js`, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(res.statusText);
      await updateCartCount();
      onDone?.();
    } catch (_) {
      form.submit();
    }
  };

  document.querySelectorAll('[data-card]').forEach((card) => {
    // Photo arrows
    const slides = card.querySelector('[data-card-slides]');
    const prev = card.querySelector('[data-card-prev]');
    const next = card.querySelector('[data-card-next]');
    if (slides && prev && next) {
      const step = (dir) => slides.scrollBy({ left: dir * slides.clientWidth, behavior: 'smooth' });
      const sync = () => {
        prev.disabled = slides.scrollLeft < 4;
        next.disabled = slides.scrollLeft + slides.clientWidth > slides.scrollWidth - 4;
      };
      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      slides.addEventListener('scroll', sync, { passive: true });
      sync();
    }

    // Quick add for products without options
    const quickForm = card.querySelector('.card__quick-form');
    quickForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = quickForm.querySelector('[data-quick-add]');
      ajaxAdd(quickForm, btn, () => {
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1600);
      });
    });

    // Option picker for products with variants
    const panel = card.querySelector('[data-card-options]');
    const openBtn = card.querySelector('[data-card-options-open]');
    if (panel && openBtn) {
      const form = panel.querySelector('form');
      const variants = JSON.parse(panel.querySelector('[data-card-variants]')?.textContent || '[]');
      const idInput = panel.querySelector('[data-variant-id]');
      const priceEl = panel.querySelector('[data-price]');
      const addBtn = panel.querySelector('[data-add]');
      const inputs = [...panel.querySelectorAll('[data-option]')];

      const setOpen = (open) => {
        panel.hidden = !open;
        openBtn.setAttribute('aria-expanded', String(open));
        if (open) panel.querySelector('input:checked, input')?.focus();
      };
      const update = () => {
        const chosen = [];
        inputs.filter((i) => i.checked).forEach((i) => (chosen[Number(i.dataset.option)] = i.value));
        const variant = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        syncOptions(panel, inputs, variants, chosen);
        if (!variant) { addBtn.disabled = true; return; }
        idInput.value = variant.id;
        priceEl.textContent = formatMoney(variant.price);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? addBtn.dataset.label : addBtn.dataset.soldOut;
      };

      openBtn.addEventListener('click', () => setOpen(panel.hidden));
      panel.querySelector('[data-card-options-close]')?.addEventListener('click', () => { setOpen(false); openBtn.focus(); });
      card.addEventListener('mouseleave', () => setOpen(false));
      panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setOpen(false); openBtn.focus(); } });
      inputs.forEach((i) => i.addEventListener('change', update));
      update();

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        ajaxAdd(form, addBtn, () => {
          addBtn.textContent = card.dataset.added || '✓';
          setTimeout(() => { setOpen(false); update(); }, 900);
        });
      });
    }
  });

  // ---------- Collection filter panel ----------
  const filterToggle = document.querySelector('[data-filter-toggle]');
  const filterPanel = document.querySelector('[data-filter-panel]');
  if (filterToggle && filterPanel) {
    filterToggle.addEventListener('click', () => {
      const open = filterPanel.hidden;
      filterPanel.hidden = !open;
      filterToggle.setAttribute('aria-expanded', String(open));
      if (open) filterPanel.querySelector('input:not([disabled])')?.focus({ preventScroll: true });
    });
    // Drop empty price fields so they don't end up as empty filters in the URL
    filterPanel.addEventListener('submit', () => {
      filterPanel.querySelectorAll('input[type="number"]').forEach((i) => { if (!i.value) i.disabled = true; });
    });
  }

  // ---------- Related products (Shopify product recommendations) ----------
  document.querySelectorAll('[data-recommendations]').forEach(async (section) => {
    try {
      const res = await fetch(section.dataset.url);
      const html = await res.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) section.innerHTML = fresh.innerHTML;
    } catch (_) { /* no recommendations: the section stays empty */ }
  });

  // ---------- Collection sorting ----------
  document.querySelector('[data-sort]')?.addEventListener('change', (e) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  // ---------- Product page ----------
  const productEl = document.querySelector('[data-product]');
  if (productEl) {
    const variants = JSON.parse(productEl.querySelector('[data-product-json]')?.textContent || '[]');
    const idInput = productEl.querySelector('[data-variant-id]');
    const priceEl = productEl.querySelector('[data-price]');
    const compareEl = productEl.querySelector('[data-compare]');
    const addBtn = productEl.querySelector('[data-add]');
    const addLabel = addBtn?.textContent.trim();
    const optionInputs = [...productEl.querySelectorAll('[data-option]')];
    // Matches Shopify's DKK money format, e.g. "1.039,00 kr"
    const number = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatter = { format: (amount) => `${number.format(amount)} kr` };

    const update = () => {
      const selected = [];
      optionInputs.filter((i) => i.checked).forEach((i) => (selected[Number(i.dataset.option)] = i.value));
      const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));
      syncOptions(productEl, optionInputs, variants, selected);
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        return;
      }
      idInput.value = variant.id;
      priceEl.textContent = formatter.format(variant.price / 100);
      compareEl.textContent = variant.compare_at_price > variant.price ? formatter.format(variant.compare_at_price / 100) : '';
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? addLabel : addBtn.dataset.soldOut || addLabel;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };
    optionInputs.forEach((i) => i.addEventListener('change', update));
    // Show sold-out combinations straight away (without touching the URL)
    {
      const initial = [];
      optionInputs.filter((i) => i.checked).forEach((i) => (initial[Number(i.dataset.option)] = i.value));
      syncOptions(productEl, optionInputs, variants, initial);
    }

    // Quantity buttons
    const qty = productEl.querySelector('#Quantity');
    productEl.querySelectorAll('[data-qty]').forEach((btn) =>
      btn.addEventListener('click', () => {
        qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(btn.dataset.qty));
      })
    );

    // Gallery thumbnails
    const main = productEl.querySelector('[data-main-image] img');
    productEl.querySelectorAll('[data-thumb]').forEach((thumb, _, all) =>
      thumb.addEventListener('click', () => {
        if (!main) return;
        main.removeAttribute('srcset');
        main.src = thumb.dataset.thumb;
        all.forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
      })
    );
  }

  // ---------- Cart: submit when quantity changes ----------
  // − / + steps the quantity; any change submits the cart form (0 removes the item)
  document.querySelectorAll('[data-cart-step]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const input = btn.parentElement.querySelector('[data-cart-qty]');
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(btn.dataset.cartStep));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    })
  );
  document.querySelectorAll('[data-cart-qty]').forEach((input) =>
    input.addEventListener('change', () => input.form?.querySelector('[data-cart-update]')?.click())
  );
})();
